/**
 * RequestCorrectionModal
 * ----------------------
 * Employee-facing modal for submitting a "missed-punch" / time-correction
 * request against one of their completed shifts.
 *
 * Backend: POST /api/timesheet-corrections — additive endpoint, never overwrites
 * the original shift values until a manager approves.
 */

import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { timeCorrectionsAPI } from '../lib/api';

const TYPE_OPTIONS = [
  { value: 'forgot_clock_in',     label: 'Forgot Clock In' },
  { value: 'forgot_clock_out',    label: 'Forgot Clock Out' },
  { value: 'incorrect_clock_in',  label: 'Incorrect Clock In' },
  { value: 'incorrect_clock_out', label: 'Incorrect Clock Out' },
  { value: 'break_missing',       label: 'Break Missing' },
  { value: 'other',               label: 'Other' },
];

function toIsoFromShiftDateAndTime(shiftDate, hhmm) {
  if (!hhmm) return '';
  if (!shiftDate) return hhmm;
  // shiftDate expected as "YYYY-MM-DD" or "—"
  const datePart = shiftDate.length >= 10 ? shiftDate.slice(0, 10) : new Date().toISOString().slice(0, 10);
  // Build a local-time ISO and convert to UTC ISO
  const localIso = `${datePart}T${hhmm}:00`;
  try {
    const d = new Date(localIso);
    if (Number.isNaN(d.getTime())) return localIso;
    return d.toISOString();
  } catch {
    return localIso;
  }
}

export default function RequestCorrectionModal({ open, onClose, shift, onSubmitted }) {
  const [type, setType] = useState('forgot_clock_in');
  const [time, setTime] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const shiftLabel = useMemo(() => {
    if (!shift) return '';
    return shift.date || (shift.start_time || '').slice(0, 10);
  }, [shift]);

  const requiresTime = type !== 'break_missing' && type !== 'other';

  const submit = async (e) => {
    e.preventDefault();
    if (!shift?.id) {
      toast.error('No shift selected');
      return;
    }
    if (requiresTime && !time) {
      toast.error('Please pick the requested time');
      return;
    }
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error('Please provide a reason (3+ characters)');
      return;
    }
    setSubmitting(true);
    try {
      const requested_value = requiresTime
        ? toIsoFromShiftDateAndTime(shiftLabel, time)
        : '—'; // placeholder for non-time corrections
      await timeCorrectionsAPI.create({
        shift_id: shift.id,
        correction_type: type,
        requested_value,
        reason: reason.trim(),
      });
      toast.success('Correction request submitted');
      setType('forgot_clock_in');
      setTime('');
      setReason('');
      onSubmitted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (!shift) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="correction-modal">
        <DialogHeader>
          <DialogTitle data-testid="correction-modal-title">Request Correction</DialogTitle>
          <DialogDescription>
            For your shift on <span className="font-medium text-slate-700">{shiftLabel}</span>
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label htmlFor="correction-type">Correction Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="correction-type" data-testid="correction-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {requiresTime && (
            <div className="space-y-1.5">
              <Label htmlFor="correction-time">Requested Time</Label>
              <Input
                id="correction-time"
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                data-testid="correction-time-input"
              />
              <p className="text-xs text-slate-500">Will be applied on {shiftLabel}.</p>
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="correction-reason">Reason <span className="text-rose-500">*</span></Label>
            <Textarea
              id="correction-reason"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Forgot to clock out before leaving."
              maxLength={2000}
              data-testid="correction-reason-input"
            />
          </div>

          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 mt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="correction-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}
              Submit Request
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
